/**
 * buildEventPayload — single-source-of-truth payload builder for create + edit
 * + the debounced `validateEventParams` pre-check.
 *
 * Eliminates drift between the three sites in the legacy `CreateEventModal`
 * (pre-check around L589, final submit around L781) and the upcoming Edit
 * flow inside `EventViewModal`. Same form state in → identical wire payload
 * out (modulo `recurringAction`).
 *
 * Recurrence semantics — backend trap protection (asymmetric Save vs Delete):
 *   - Edit + recurring + NO `canManageRecurrent`: caller passes `recurringScope='current'`
 *     so backend permission gate accepts the request.
 *   - Edit + recurring + HAS `canManageRecurrent`: caller passes the user-picked scope
 *     ('current' | 'not-started' | 'following').
 *   - Create: legacy uses `recurringAction='add'` (NOT 'save').
 *
 * Mode → recurringAction wire shape:
 *   - `mode: 'create'`   → `recurringAction: 'add'`, no `eventsForAction`.
 *   - `mode: 'precheck'` → create-context precheck only.
 *                          `recurringAction: 'add'`, no `eventsForAction`.
 *   - `mode: 'edit'`     → final edit submit AND edit-context precheck
 *                          (`check-event-params/{eventId}`). Backend keys off
 *                          the existing event id in the URL to skip-self;
 *                          sending 'add' there makes it treat the series as
 *                          new and report its own future instances as
 *                          conflicts. Emits `recurringAction: 'save'` and,
 *                          when the form is recurring, `eventsForAction:
 *                          recurringScope || 'current'`.
 *
 * @module buildEventPayload
 */
import { cdtToEpoch, formatYMD } from './dateTime.js';

/**
 * @typedef {Object} EventFormState
 * @property {string} formOrgUnique
 * @property {string} eventType  'air' | 'maintenance'
 * @property {Object} startTime  CalendarDateTime
 * @property {Object} finishTime  CalendarDateTime
 * @property {string|number|null} locationId
 * @property {string|number|null} pilotId
 * @property {string|number|null} sicPilotId
 * @property {string|number|null} instructorId
 * @property {string|null} activityType  Bare activity-type ID (optional; regular events only)
 * @property {boolean} eventSharing  Invite pilots to participate (regular events only)
 * @property {Array<string|number>} resourceIds
 * @property {string} comments
 * @property {boolean} recurring
 * @property {string} recurringType  'day' | 'weeks' | 'months' | 'years'
 * @property {Array<string>} daysOfWeek  ['0'..'6'] (Sun=0)
 * @property {string} repsType  'number' | 'date'
 * @property {string|number} repsInterval
 * @property {string|number} reps
 * @property {Object|null} recurringEndDate  CalendarDate
 * @property {Object<string, *>} customValues  keyed by customField id
 *
 * @typedef {Object} BuildOpts
 * @property {'create'|'edit'|'precheck'} mode
 * @property {string} timezone  Olson tz (filter/wire tz — unchanged)
 * @property {string} [eventLocationTz]  IANA tz for picker input/output epoch conversion
 * @property {boolean} canManageRecurrent
 * @property {Array<{id:number|string,type:string,required?:boolean}>} customFields
 * @property {string} [recurringScope]  Edit-only — 'current'|'not-started'|'following'
 *   Required when `mode==='edit'` AND form is recurring (see notes above).
 */

/**
 * Build the wire-format payload from form state.
 *
 * Returns the payload object — caller routes it to `createEvent`,
 * `createMyEvent`, `updateEvent`, `updateMyEvent`, or `validateEventParams`.
 *
 * @param {EventFormState} formState
 * @param {BuildOpts} opts
 * @returns {Object} payload
 */
export function buildEventPayload(formState, opts) {
	const {
		formOrgUnique,
		eventType,
		startTime,
		finishTime,
		locationId,
		pilotId,
		sicPilotId,
		instructorId,
		activityType,
		eventSharing,
		resourceIds,
		comments,
		recurring,
		recurringType,
		daysOfWeek,
		repsType,
		repsInterval,
		reps,
		recurringEndDate,
		customValues
	} = formState || {};

	const {
		mode,
		timezone,
		eventLocationTz,
		canManageRecurrent,
		customFields,
		recurringScope,
		forceAddToWaiting
	} = opts || {};

	const isMaintenance = eventType === 'maintenance';
	const startEpoch = cdtToEpoch(startTime, eventLocationTz);
	const endEpoch = cdtToEpoch(finishTime, eventLocationTz);

	// Mirror the modal's gate: maintenance NEVER recurs; recurrence requires
	// the calendarManageRecurrentEvent permission. State-leak protection.
	const willRecur = !isMaintenance && !!recurring && !!canManageRecurrent;

	const recurringEndDateStr =
		willRecur && repsType === 'date' && recurringEndDate ? formatYMD(recurringEndDate) : '';

	const customFieldsPayload = isMaintenance
		? []
		: (customFields || [])
				.map(cf => {
					let v = customValues?.[cf.id];
					if (cf.type === 'date' && v && typeof v === 'object' && v.year != null) {
						v = formatYMD(v);
					}
					return { typeId: Number(cf.id), value: v ?? '' };
				})
				.filter(cf => cf.value !== '' && cf.value != null);

	const payload = {
		timezone,
		// Resource location timezone — separate from `timezone` (which is the
		// filter / browser tz used for response formatting). Maintenance
		// callers MUST use this for `formatMaintenanceDate`: Symfony's
		// `MaintenanceManager.php:759-769` reinterprets the wall-clock string
		// in the resource location tz, so a filter-tz-formatted string would
		// shift by the tz delta on browsers ≠ resource location.
		locationTimezoneId: eventLocationTz || timezone,
		organizationUnique: formOrgUnique,
		startTime: startEpoch,
		finishTime: endEpoch,
		// Maintenance is single-resource (legacy parity); regular events accept many.
		resources: isMaintenance
			? resourceIds && resourceIds.length
				? [Number(resourceIds[0])]
				: []
			: (resourceIds || []).map(Number),
		comments: comments || '',
		customFields: customFieldsPayload,
		type: isMaintenance ? 'maintenance' : 'air',
		addToWaiting: !!forceAddToWaiting,
		recurring: willRecur,
		recurringType: willRecur ? recurringType : 'day',
		recurringEndDate: recurringEndDateStr,
		repsInterval: willRecur ? Number(repsInterval) || 1 : 1,
		repsType: willRecur ? repsType : 'number',
		reps: willRecur && repsType === 'number' ? Number(reps) || 0 : 0,
		// Defensive filter: only valid Sun=0..Sat=6 keys reach the wire — a
		// stray "NaN" from a bad weekday computation must never ship.
		daysOfWeek:
			willRecur && recurringType === 'weeks'
				? [...(daysOfWeek || [])].filter(d => /^[0-6]$/.test(String(d))).sort()
				: [],
		locationID: locationId ? Number(locationId) : null,
		pilotID: isMaintenance ? null : pilotId ? Number(pilotId) : null,
		secondInCommandID: isMaintenance ? null : sicPilotId || null,
		instructorID: isMaintenance ? null : instructorId ? Number(instructorId) : null,
		// Activity Type — form state holds a bare ID string (day-view parity).
		// Optional; maintenance events never carry one.
		// `EventParamsRequestDto.activityType` is `int|null`, so cast to a
		// number; empty/unselected must serialize to `null` (never `0`).
		activityType: isMaintenance || !activityType ? null : Number(activityType),
		// Event Sharing — invite pilots to participate (day-view parity, gated
		// on AIR event type). Sent as 1/0. Maintenance never shares.
		eventSharing: !isMaintenance && eventSharing ? 1 : 0
	};

	// `recurringAction` differs by mode:
	//   create / precheck → 'add' (legacy create modal contract)
	//   edit              → 'save'
	// Non-recurring (any mode) — legacy still sets the action field; we
	// preserve that so the DTO stays consistent with the previous shape.
	if (mode === 'edit') {
		payload.recurringAction = 'save';
	} else {
		payload.recurringAction = 'add';
	}

	// Edit + recurring → eventsForAction. Backend trap protection:
	//   - With permission: send the user-picked scope.
	//   - Without permission: send 'current' explicitly (omitting fails
	//     `null !== CURRENT` permission gate). Caller is responsible for
	//     passing `recurringScope='current'` in that case.
	// Non-recurring or create / precheck → omit (DTO default is correct).
	if (mode === 'edit' && willRecur) {
		payload.eventsForAction = recurringScope || 'current';
	}

	return payload;
}
